import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, LoadingSpinner } from '@shared/components';
import { PermissionGuard } from '@features/auth';
import { useQuote } from '../../context/QuoteContext';
import { useQuoteBasketPricing } from '../../hooks/useBasketPricing';
import { isLiveQuote } from '../../utils/quoteStatus';
import { useCompleteSale } from '../../hooks/useCompleteSale';
import { useCheckoutOutcomeFocus } from '../../hooks/useCheckoutOutcomeFocus';
import type { AgreementConfirmation } from '../../hooks/useAgreementConfirmation';
import type { AgreementActivation } from '../../hooks/useAgreementActivation';
import type { CheckoutOutcome } from '../../hooks/useCheckoutFrameEvents';
import type { QuoteJourney } from '../../hooks/useElemisCheckout';
import type { ElemisRefusal } from '@features/agreements';
import { QuoteSummaryCard } from '../QuoteSummaryCard';
import { AgreementPendingNotice } from './AgreementPendingNotice';
import { ElemisCheckoutRefusalAlert } from './ElemisCheckoutRefusalAlert';
import {
    readyCheckoutStatusText,
    verificationStatusText,
} from '../../utils/elemisCheckoutStatusText';

interface QuoteSummaryStepProps {
    showActions?: boolean;
    // Agreements Slice 3 (#901). REQUIRED, not optional with a default
    // (design F-D2) — `journey` decides which of two terminal actions a sale
    // takes, and one of them mints a live Agreement for money nobody has
    // collected if chosen wrongly. A forgotten prop here is a
    // `tsc --noEmit` failure, not a silent default.
    journey: QuoteJourney;
    isInitiating: boolean;
    refusal: ElemisRefusal | null;
    /** True once a checkoutUrl exists: the status region then reads "ready"
     *  and the hosted checkout itself lives in ElemisCheckoutModal. */
    checkoutStarted: boolean;
    agreementRef: string | null;
    linkSuppressedByReplay?: boolean;
    /** The frame's terminal report and, after `completed`, what the server has
     *  confirmed so far. */
    outcome: CheckoutOutcome | null;
    activation: AgreementActivation;
    onInitiateCheckout: () => void;
    onRetryCheckout: () => void;
    /** A retry is settling the save lane; locks the outcome alert's Retry. */
    retryBusy?: boolean;
    agreementConfirmation: AgreementConfirmation;
}

export const QuoteSummaryStep: React.FC<QuoteSummaryStepProps> = ({
    showActions = false,
    journey,
    isInitiating,
    refusal,
    checkoutStarted,
    agreementRef,
    linkSuppressedByReplay = false,
    outcome,
    activation,
    onInitiateCheckout,
    onRetryCheckout,
    retryBusy = false,
    agreementConfirmation,
}) => {
    const quote = useQuote();
    const navigate = useNavigate();
    // A completed/dead quote is no longer priceable against today's catalogue — the
    // as-sold figures already sit on the restored lines, not a fresh smr response.
    const pricing = useQuoteBasketPricing(isLiveQuote(quote.status));
    const { completeSale, isLoading } = useCompleteSale();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const statusRef = useRef<HTMLDivElement>(null);
    useCheckoutOutcomeFocus(outcome, statusRef);

    const handleCompleteQuote = async () => {
        if (!quote.canCompleteQuote()) {
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await completeSale({
                quoteId: quote.quoteId!,
            });

            if (result.id) {
                // Handle successful quote completion
                alert(`Quote completed successfully! Quote ID: ${result.id}`);
                quote.reset();
            }
        } catch (err) {
            console.error('Failed to complete quote:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isAwaitingFirstPrice = quote.isPricingPending && pricing.data === undefined;
    if (isAwaitingFirstPrice) {
        return <LoadingSpinner message="Loading the latest price..." className="py-12" />;
    }

    if (!quote.canCompleteQuote() && !quote.isPricingPending) {
        return (
            <div className="text-center py-12">
                <div className="text-mp-muted mb-4">
                    <svg
                        className="mx-auto h-12 w-12"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                    </svg>
                </div>
                <h3 className="text-lg font-medium text-mp-foreground mb-2">Incomplete Information</h3>
                <p className="text-mp-muted">
                    Please complete all previous steps before reviewing the quote summary.
                </p>
            </div>
        );
    }

    // The verification read's own ref outranks the one initiation reported.
    const confirmedRef =
        activation.state === 'active'
        || activation.state === 'completed'
        || activation.state === 'finalisingTimeout'
            ? activation.agreementRef
            : null;
    const statusText =
        outcome === 'completed' && activation.state !== 'idle' && activation.state !== 'completed'
            ? verificationStatusText(activation.state, confirmedRef ?? agreementRef)
            : checkoutStarted && outcome === null && !isInitiating
                ? readyCheckoutStatusText(agreementRef, linkSuppressedByReplay)
                : null;
    const failureOutcome = outcome !== null && outcome !== 'completed' ? outcome : null;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold text-mp-foreground mb-2">Quote Summary</h1>
                <p className="text-mp-muted">
                    Review all the details below and complete the quote to register the service
                    plan.
                </p>
            </div>

            {/* Quote Summary Card */}
            <QuoteSummaryCard
                dealer={quote.selectedDealer!}
                vehicle={quote.selectedVehicle!}
                product={quote.selectedProduct!}
                services={quote.configuredServices}
                addOns={quote.configuredAddOns}
                payment={quote.paymentOptions}
                // #739: the confirmed scheduling method. `payment` above is
                // retained only so `QuoteSummaryCardProps` stays derivable
                // from `QuoteSummary` (D9) — it is no longer read for a
                // startDate field (#766 discharged that). Its retirement is
                // N1, tracked separately.
                paymentSelection={quote.paymentSelection}
                customer={quote.selectedCustomer!}
                pricing={pricing.data}
            />

            {agreementConfirmation.state === 'resolved' && (
                <Alert variant="success" role="status" className="justify-between">
                    <p className="font-medium">This sale&apos;s agreement has been created.</p>
                    <PermissionGuard permission={{ resource: 'agreement', action: 'read' }}>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/agreements/${agreementConfirmation.agreementId}`)}
                        >
                            View agreement
                        </Button>
                    </PermissionGuard>
                </Alert>
            )}
            {agreementConfirmation.state === 'missing' && (
                <AgreementPendingNotice quoteRef={quote.quoteRef ?? quote.quoteId ?? ''} />
            )}

            {/* The status ladder and a first-attempt refusal; the checkout itself
                opens in ElemisCheckoutModal over this step once a URL exists. */}
            {journey === 'motorplan_collects' && quote.status !== 'completed' && (
                <section aria-busy={isInitiating} className="space-y-4 border-t border-mp-border pt-6">
                    <h2 className="text-lg font-semibold text-mp-foreground">
                        Pay online with MotorPlan Payments
                    </h2>

                    {/* role="status" — polite, progress not a problem.
                        Permanently mounted; absent children when idle. */}
                    <div ref={statusRef} tabIndex={-1} role="status" className="text-sm text-mp-muted">
                        {statusText && <p>{statusText}</p>}
                    </div>

                    {(activation.state === 'timeout' || activation.state === 'finalisingTimeout') && (
                        <Button size="sm" variant="outline" onClick={activation.checkAgain}>
                            Check again
                        </Button>
                    )}

                    <ElemisCheckoutRefusalAlert
                        refusal={refusal}
                        onRetry={onInitiateCheckout}
                        outcome={failureOutcome}
                        isRetrying={isInitiating || retryBusy}
                        onRetryOutcome={onRetryCheckout}
                    />
                </section>
            )}

            {/* Action Buttons — dead on the shipped journey (V1): the live
                Complete Quote button is QuoteProgressNav's. Gated to
                `dealer_collects` (gap G4) rather than deleted, so it cannot
                become a second way to complete a MotorPlan-collects sale if
                a future caller passes `showActions` for the dealer-collects
                path. */}
            {showActions && journey === 'dealer_collects' && (
                <div className="flex justify-between items-center pt-6 border-t border-mp-border">
                    <Button variant="outline" onClick={quote.reset}>
                        Start New Quote
                    </Button>

                    <Button
                        onClick={handleCompleteQuote}
                        disabled={isSubmitting || isLoading}
                        className="bg-mp-success text-mp-success-content hover:bg-hover-mp-success"
                    >
                        {isSubmitting || isLoading ? (
                            <>
                                <svg
                                    className="animate-spin -ml-1 mr-3 h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                Completing Quote...
                            </>
                        ) : (
                            'Complete Quote'
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
};
